import * as React from 'react';
import {useLayeringContext, type Layers} from 'src/hooks/useLayeringContext';
import {elevationBackdrop} from '@spaced-out/ui-design-system';
import {useSelector} from 'react-redux';
import {
  selectSideNavExpanded,
  selectFullscreen,
} from 'src/selectors/app-config';
import {
  PANEL_LEFT_MARGIN,
  SIDENAV_EXPANDED_WIDTH,
  SIDENAV_COLLAPSED_WIDTH,
  DEFAULT_ASK_AI_ELEVATION,
} from 'src/utils/ask-ai';


function getAskAiElevation(layers: Layers): number {
  if (layers.Modals.length > 0 || layers.Dialogs.length > 0) {
    //make this 0 so that it hides behind the backdrop
    //it can be any z-index value that is less than the backdrop
    return 0;
  }
  const arePanelsOpen = layers.Panels.length > 0;
  const anyPanelHasBackdrop = layers.Panels.some((panel) => panel.hasBackdrop);

  if (arePanelsOpen && anyPanelHasBackdrop) {
    return layers.Panels.length * parseInt(elevationBackdrop, 10) + 1;
  }
  return DEFAULT_ASK_AI_ELEVATION;
}

function getAskAiMarginLeft(layers: Layers): number | undefined {
  if (layers.Panels.length > 0) {
    for (const panel of layers.Panels) {
      if (panel.hasBackdrop) {
        return 0;
      }
    }
  }
  return undefined;
}

export const useAskAiPositioningContext = (): {
  elevation: number;
  leftMargin: number | undefined;
} => {
  const isSideNavExpanded = useSelector(selectSideNavExpanded);
  const isFullScreenAppExperience = useSelector(selectFullscreen);
  const layers = useLayeringContext();

  const marginLeftWithLayers = getAskAiMarginLeft(layers);
  const leftMargin = React.useMemo(() => {
    if (typeof marginLeftWithLayers !== 'undefined') {
      return marginLeftWithLayers;
    }
    if (isFullScreenAppExperience) {
      return PANEL_LEFT_MARGIN;
    }

    return (
      (isSideNavExpanded ? SIDENAV_EXPANDED_WIDTH : SIDENAV_COLLAPSED_WIDTH) +
      PANEL_LEFT_MARGIN
    );
  }, [isFullScreenAppExperience, isSideNavExpanded, marginLeftWithLayers]);
  const elevation = getAskAiElevation(layers);
  return {elevation, leftMargin};
};
